import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useGTMContext } from '../context/GTMContext';
import { info, error as logError } from '../utils/logger';
import futbolistaService from '../services/futbolistaService';
import MobileOptimizedInput from '../components/MobileOptimizedInput';
import MobileOptimizedButton from '../components/MobileOptimizedButton';
import useWindowSize from '../hooks/useWindowSize';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    remember: false,
  });
  const [formError, setFormError] = useState('');
  const { login, loading } = useAuth();
  const gtm = useGTMContext();
  const navigate = useNavigate();
  const { isMobile } = useWindowSize();

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    // Validación básica
    if (!formData.email || !formData.password) {
      setFormError('Por favor completa todos los campos');
      return;
    }

    try {
      info('Intentando iniciar sesión', { email: formData.email });

      // Mostrar mensaje de carga
      setFormError('Iniciando sesión...');

      const response = await login({
        email: formData.email,
        password: formData.password,
        remember: formData.remember,
      });
      info('Login exitoso', { userId: response?.user?._id, role: response?.user?.role });

      // Enviar evento GTM de login exitoso
      gtm.sendLoginEvent('email', response?.user?.role);

      // Limpiar mensaje de error
      setFormError('');

      // Verificar el rol del usuario y redirigir apropiadamente
      if (response && response.user && response.user.role === 'admin') {
        // Si es administrador, redirigir al panel de administración
        info('Redirigiendo usuario admin al panel de administración');
        navigate('/admin');
      } else if (response && response.user && response.user.role === 'coach') {
        // Si es entrenador, redirigir a su dashboard específico
        info('Redirigiendo entrenador a su dashboard');
        navigate('/coach');
      } else if (response && response.user && response.user.role === 'player') {
        // Si es un futbolista, redirigir a su dashboard específico
        info('Redirigiendo futbolista a su dashboard');
        navigate('/player');
      } else if (response && response.user && response.user.role === 'parent') {
        // Si es un tutor/padre, verificar si tiene futbolistas
        try {
          const futbolistas = await futbolistaService.getFutbolistas();
          if (futbolistas && futbolistas.length > 0) {
            // Si tiene futbolistas, ir directamente al dashboard de tutor
            info('Redirigiendo tutor con futbolistas al dashboard');
            navigate('/tutor');
          } else {
            // Si no tiene futbolistas, ir a la página de ayuda
            info('Redirigiendo tutor sin futbolistas a la página de ayuda');
            navigate('/help');
          }
        } catch (error) {
          // En caso de error, ir a la página de ayuda por defecto
          logError('Error al verificar futbolistas', { error: error.message });
          navigate('/help');
        }
      } else {
        // Si es un usuario regular, redirigir a la página principal
        info('Redirigiendo usuario regular a la página principal');
        navigate('/');
      }
    } catch (error) {
      logError('Error en login', {
        error: error.message,
        email: formData.email,
        stack: error.stack
      });

      // Manejar diferentes tipos de errores
      if (error.message) {
        // Si el error tiene un mensaje, mostrarlo
        setFormError(error.message);
      } else if (error.response && error.response.data && error.response.data.message) {
        // Si el error viene de la API y tiene un mensaje, mostrarlo
        setFormError(error.response.data.message);
      } else if (error.type === 'network') {
        // Si es un error de red, mostrar un mensaje específico
        setFormError(
          'No se pudo conectar al servidor. Por favor, verifica tu conexión a internet e inténtalo de nuevo.',
        );
      } else {
        // Mensaje genérico para otros errores
        setFormError('Error al iniciar sesión. Por favor, inténtalo de nuevo.');
      }
    }
  };

  return (
    <section className="auth-section">
      <div className="container">
        <div className="auth-container">
          <div className="auth-header">
            <h2>Iniciar Sesión</h2>
            <p>Accede a tu cuenta para inscribirte en partidos y ver tu progreso</p>
          </div>

          {formError && (
            <div
              className={`alert ${formError === 'Iniciando sesión...' ? 'alert-info' : 'alert-danger'}`}
              style={{
                marginBottom: '20px',
                padding: '10px 15px',
                borderRadius: '5px',
                textAlign: 'center',
              }}
            >
              {formError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form" style={{
            maxWidth: isMobile ? '100%' : '400px',
            margin: '0 auto',
            padding: isMobile ? '0' : '20px',
          }}>
            <MobileOptimizedInput
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              label="Correo Electrónico"
              placeholder="tu@email.com"
              required
              autoComplete="email"
              inputMode="email"
            />

            <MobileOptimizedInput
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              label="Contraseña"
              placeholder="Tu contraseña"
              required
              autoComplete="current-password"
            />

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '24px',
              padding: isMobile ? '8px 0' : '4px 0',
            }}>
              <input
                type="checkbox"
                id="remember"
                name="remember"
                checked={formData.remember}
                onChange={handleChange}
                style={{
                  width: isMobile ? '20px' : '16px',
                  height: isMobile ? '20px' : '16px',
                  cursor: 'pointer',
                }}
              />
              <label
                htmlFor="remember"
                style={{
                  fontSize: isMobile ? '16px' : '14px',
                  color: '#495057',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                Recordarme
              </label>
            </div>

            <MobileOptimizedButton
              type="submit"
              variant="primary"
              size="large"
              fullWidth
              loading={loading}
              disabled={loading}
              icon="login"
              style={{ marginBottom: '24px' }}
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </MobileOptimizedButton>

            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: isMobile ? '16px' : '20px',
              marginBottom: '24px',
            }}>
              <Link
                to="/forgot-password"
                style={{
                  color: '#4352A9',
                  textDecoration: 'none',
                  fontSize: isMobile ? '16px' : '14px',
                  fontWeight: '500',
                  padding: isMobile ? '8px 0' : '4px 0',
                  minHeight: isMobile ? '44px' : 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'color 0.3s ease',
                }}
                onMouseOver={(e) => e.target.style.color = '#061156'}
                onMouseOut={(e) => e.target.style.color = '#4352A9'}
              >
                ¿Olvidaste tu contraseña?
              </Link>
              <Link
                to="/register"
                style={{
                  color: '#4352A9',
                  textDecoration: 'none',
                  fontSize: isMobile ? '16px' : '14px',
                  fontWeight: '500',
                  padding: isMobile ? '8px 0' : '4px 0',
                  minHeight: isMobile ? '44px' : 'auto',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'color 0.3s ease',
                }}
                onMouseOver={(e) => e.target.style.color = '#061156'}
                onMouseOut={(e) => e.target.style.color = '#4352A9'}
              >
                ¿No tienes cuenta? Regístrate
              </Link>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              margin: '24px 0',
              textAlign: 'center',
            }}>
              <div style={{
                flex: 1,
                height: '1px',
                backgroundColor: '#e1e5e9',
              }}></div>
              <span style={{
                padding: '0 16px',
                fontSize: isMobile ? '14px' : '12px',
                color: '#6c757d',
                backgroundColor: '#ffffff',
              }}>
                O inicia sesión con
              </span>
              <div style={{
                flex: 1,
                height: '1px',
                backgroundColor: '#e1e5e9',
              }}></div>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: '12px',
              marginBottom: '20px',
            }}>
              <MobileOptimizedButton
                type="button"
                variant="secondary"
                size="medium"
                fullWidth={isMobile}
                icon="login"
                style={{
                  backgroundColor: '#ffffff',
                  color: '#db4437',
                  border: '2px solid #db4437',
                }}
              >
                Google
              </MobileOptimizedButton>
              <MobileOptimizedButton
                type="button"
                variant="secondary"
                size="medium"
                fullWidth={isMobile}
                icon="login"
                style={{
                  backgroundColor: '#ffffff',
                  color: '#4267B2',
                  border: '2px solid #4267B2',
                }}
              >
                Facebook
              </MobileOptimizedButton>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Login;
